import { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '../../svgIcons/Close';

const StatsPopup = ({ season, isVisible, onClose }) => {
    const popupRef = useRef(null);

    // Handle click outside to close popup
    const handleClickOutside = useCallback(
        event => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose();
            }
        },
        [onClose]
    );

    // Handle escape key to close popup
    const handleEscapeKey = useCallback(
        event => {
            if (event.key === 'Escape') {
                onClose();
            }
        },
        [onClose]
    );

    // Setup event listeners when popup is visible
    useEffect(() => {
        if (!isVisible) return;

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isVisible, handleClickOutside, handleEscapeKey]);

    // Handle body scroll when popup is open
    useEffect(() => {
        if (isVisible) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isVisible]);

    if (!isVisible) return null;

    // Stats data configuration
    const statsData = [
        { label: 'Appearances', value: season?.appearances || 0 },
        { label: 'Goals', value: season?.goals || 0 },
        { label: 'Assists', value: season?.assists || 0 },
        { label: 'Clean Sheets', value: season?.cleanSheets || 'N/A' },
        { label: 'Minutes Played', value: season?.minsPlayed || 0 }
    ];

    const seasonName = season?.seasonName || season?.name || 'Unknown';

    return (
        <>
            {/* Backdrop */}
            <div
                className='fixed inset-0 bg-black/50 z-[999] transition-opacity duration-200'
                onClick={onClose}
                aria-label='Close popup backdrop'
            />

            {/* Popup Modal */}
            <div
                ref={popupRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='stats-popup-title'
                className='fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1000] 
                          w-[330px] bg-white rounded-[32px] border-[6px] border-[#4AFA0F] 
                          shadow-2xl transition-all duration-200 scale-100'
            >
                {/* Header with Close Button */}
                <header className='flex justify-end pt-4 px-4'>
                    <button
                        onClick={onClose}
                        className='bg-gray-200 hover:bg-gray-300 rounded-full p-1 
                                  transition-colors duration-200 focus:outline-none 
                                  focus:ring-2 focus:ring-gray-400'
                        aria-label='Close stats popup'
                    >
                        <CloseIcon width={22} height={22} />
                    </button>
                </header>

                {/* Content */}
                <main className='px-6 pt-1 pb-9 text-black'>
                    <h2
                        id='stats-popup-title'
                        className='text-xl font-normal text-center mb-4'
                    >
                        Season {seasonName}
                    </h2>

                    {/* Stats List */}
                    <div className='space-y-3'>
                        {statsData.map(({ label, value }) => (
                            <StatRow key={label} label={label} value={value} />
                        ))}
                    </div>
                </main>
            </div>
        </>
    );
};

// Helper component for individual stat rows
const StatRow = ({ label, value }) => (
    <div className='flex justify-between items-center'>
        <span className='text-sm font-normal text-gray-700'>{label}</span>
        <span className='text-sm font-medium text-black'>{value}</span>
    </div>
);

// PropTypes for type checking
StatsPopup.propTypes = {
    season: PropTypes.shape({
        seasonName: PropTypes.string,
        name: PropTypes.string,
        appearances: PropTypes.number,
        goals: PropTypes.number,
        assists: PropTypes.number,
        cleanSheets: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        minsPlayed: PropTypes.number
    }),
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

StatRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default StatsPopup;
